import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Send, Loader2, Camera, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { askQuestion } from "@/lib/api";

export default function AskPage({ deviceId }) {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [question, setQuestion] = useState("");
  const [imageBase64, setImageBase64] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [conversations, setConversations] = useState([]);

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setImagePreview(ev.target.result);
      setImageBase64(ev.target.result.split(",")[1]);
    };
    reader.readAsDataURL(file);
  };

  const clearImage = () => {
    setImagePreview(null);
    setImageBase64(null);
  };

  const handleAsk = async () => {
    if (!question.trim()) return;
    setLoading(true);
    const q = question;
    const img = imageBase64;
    setQuestion("");
    setConversations((prev) => [...prev, { type: "question", text: q, image: imagePreview }]);
    clearImage();

    try {
      const data = await askQuestion(q, deviceId, img);
      setConversations((prev) => [...prev, { type: "answer", text: data.answer }]);
    } catch (err) {
      toast.error("Could not get an answer. Please try again.");
      setConversations((prev) => [...prev, { type: "answer", text: "Sorry, I couldn't process that question. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  const suggestedQuestions = [
    "When should I prune my roses?",
    "What plants grow well in shade?",
    "How often should I water tomatoes?",
    "Best plants for beginners?",
  ];

  return (
    <div className="flex flex-col h-[calc(100vh-5rem)]" data-testid="ask-page">
      {/* Header */}
      <div className="px-5 pt-6 pb-4 flex items-center gap-3">
        <button onClick={() => navigate(-1)} data-testid="ask-back-btn" className="w-12 h-12 rounded-xl border-2 border-[#0A2918] flex items-center justify-center press-active hard-shadow-sm bg-white">
          <ArrowLeft size={24} strokeWidth={2.5} />
        </button>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#0A2918]" style={{ fontFamily: 'Work Sans' }}>
          Ask a Question
        </h1>
      </div>

      {/* Chat Area */}
      <div className="flex-1 overflow-y-auto px-5 space-y-4 no-scrollbar">
        {conversations.length === 0 && (
          <div className="space-y-3" data-testid="suggested-questions">
            <p className="text-lg font-bold text-[#0A2918]" style={{ fontFamily: 'Work Sans' }}>
              Try asking...
            </p>
            {suggestedQuestions.map((sq, i) => (
              <button
                key={i}
                onClick={() => setQuestion(sq)}
                data-testid={`suggestion-${i}`}
                className="w-full text-left bg-[#F2F7F4] border-2 border-[#0A2918] rounded-xl p-4 text-base font-medium text-[#0A2918] press-active hard-shadow-sm transition-colors hover:bg-[#E8EFEA]"
              >
                {sq}
              </button>
            ))}
          </div>
        )}

        {conversations.map((msg, i) => (
          <div
            key={i}
            className={`animate-slide-up ${msg.type === "question" ? "flex justify-end" : "flex justify-start"}`}
          >
            <div className={`max-w-[85%] rounded-2xl p-4 ${
              msg.type === "question"
                ? "bg-[#0A2918] text-white"
                : "bg-white border-2 border-[#0A2918] hard-shadow-sm"
            }`}>
              {msg.image && (
                <img src={msg.image} alt="" className="w-full h-32 object-cover rounded-xl mb-2" />
              )}
              <p className={`text-base leading-relaxed whitespace-pre-wrap ${
                msg.type === "answer" ? "text-[#0A2918]" : ""
              }`}>{msg.text}</p>
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div className="bg-white border-2 border-[#0A2918] rounded-2xl p-4 hard-shadow-sm animate-gentle-pulse">
              <div className="flex items-center gap-2 text-[#2A4D3B]">
                <Loader2 className="animate-spin" size={20} />
                <span className="text-base font-medium">Thinking...</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Image Preview */}
      {imagePreview && (
        <div className="px-5 pt-2">
          <div className="relative inline-block">
            <img src={imagePreview} alt="" className="h-16 w-16 object-cover rounded-xl border-2 border-[#0A2918]" />
            <button
              onClick={clearImage}
              className="absolute -top-2 -right-2 w-6 h-6 bg-[#E25F45] text-white rounded-full flex items-center justify-center"
              data-testid="clear-image-btn"
            >
              <X size={14} strokeWidth={3} />
            </button>
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="px-5 py-4 border-t-2 border-[#0A2918] bg-white">
        <div className="flex gap-2">
          <button
            onClick={() => fileInputRef.current?.click()}
            data-testid="ask-camera-btn"
            className="w-14 h-14 flex-shrink-0 rounded-xl border-2 border-[#0A2918] flex items-center justify-center press-active hard-shadow-sm bg-[#F2F7F4]"
          >
            <Camera size={24} strokeWidth={2.5} className="text-[#0A2918]" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleImageSelect}
            className="hidden"
          />
          <input
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !loading && handleAsk()}
            placeholder="Ask about plants, care, soil..."
            data-testid="ask-input"
            className="flex-1 h-14 rounded-xl border-2 border-[#0A2918] px-4 text-lg font-medium bg-white text-[#0A2918] placeholder:text-[#2A4D3B]/50 focus:ring-4 focus:ring-[#FFA62B] focus:outline-none"
          />
          <Button
            onClick={handleAsk}
            disabled={loading || !question.trim()}
            data-testid="ask-send-btn"
            className="w-14 h-14 flex-shrink-0 rounded-xl bg-[#E25F45] text-white press-active hard-shadow-accent"
          >
            <Send size={22} strokeWidth={2.5} />
          </Button>
        </div>
      </div>
    </div>
  );
}
